import * as React from 'react';
import { ApiError } from '@/lib/api/client';

/**
 * Waiting for the requirements packet (T-0183). The PDF is rendered by a
 * worker; until it is, the API answers 409 `signoff.packet_not_ready`. Used
 * by the staff screen's packet buttons and the signatory's page.
 *
 * `start` asks once. A file that comes back at once goes straight to
 * `onFile`, still close to the click, so a new tab or a download is allowed.
 * While the packet is being prepared it asks again every `PACKET_POLL_MS`,
 * for up to `PACKET_WAIT_MS`, then gives up with the server's message. Any
 * other answer ends the wait at once with its message: in particular 409
 * `signoff.packet_failed`, a render that failed a moment ago, which the
 * server does not retry for a few minutes, so asking again would not help. A
 * file that comes back after a wait is kept as `ready`, for the page to
 * offer as a button: browsers block a tab opened long after the click.
 */

export const PACKET_POLL_MS = 5_000;
export const PACKET_WAIT_MS = 3 * 60_000;

export function isPacketNotReady(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.status === 409 &&
    err.details?.['reason'] === 'signoff.packet_not_ready'
  );
}

export type PacketWait<T> =
  | { kind: 'idle' }
  /** The first ask. */
  | { kind: 'fetching' }
  /** Being rendered; asked again every `PACKET_POLL_MS`. */
  | { kind: 'preparing' }
  /** Arrived after a wait; the page offers it as a button. */
  | { kind: 'ready'; file: T }
  | { kind: 'failed'; error: unknown };

export interface PacketWaitHandlers<T> {
  fetchPacket: () => Promise<T>;
  /** The file, when it came back at once. */
  onFile: (file: T) => void;
  /** True when the page dealt with the error itself (a refused link). */
  onError?: (err: unknown) => boolean;
}

export function usePacketWait<T>(handlers: PacketWaitHandlers<T>): {
  wait: PacketWait<T>;
  start: () => void;
  reset: () => void;
} {
  const [wait, setWait] = React.useState<PacketWait<T>>({ kind: 'idle' });
  const latest = React.useRef(handlers);
  latest.current = handlers;
  // Each start gets a number; a newer start, a reset or unmounting retires it.
  const run = React.useRef(0);
  const timer = React.useRef<number | undefined>(undefined);

  const stop = React.useCallback(() => {
    run.current += 1;
    if (timer.current !== undefined) window.clearTimeout(timer.current);
    timer.current = undefined;
  }, []);
  React.useEffect(() => stop, [stop]);

  const start = React.useCallback(() => {
    stop();
    const id = run.current;
    const startedAt = Date.now();
    setWait({ kind: 'fetching' });
    const ask = async (first: boolean): Promise<void> => {
      let file: T;
      try {
        file = await latest.current.fetchPacket();
      } catch (err) {
        if (run.current !== id) return;
        if (isPacketNotReady(err) && Date.now() - startedAt < PACKET_WAIT_MS) {
          setWait({ kind: 'preparing' });
          timer.current = window.setTimeout(() => {
            timer.current = undefined;
            void ask(false);
          }, PACKET_POLL_MS);
          return;
        }
        if (latest.current.onError?.(err)) {
          setWait({ kind: 'idle' });
          return;
        }
        setWait({ kind: 'failed', error: err });
        return;
      }
      if (run.current !== id) return;
      if (!first) {
        setWait({ kind: 'ready', file });
        return;
      }
      setWait({ kind: 'idle' });
      try {
        latest.current.onFile(file);
      } catch (err) {
        setWait({ kind: 'failed', error: err });
      }
    };
    void ask(true);
  }, [stop]);

  const reset = React.useCallback(() => {
    stop();
    setWait({ kind: 'idle' });
  }, [stop]);

  return { wait, start, reset };
}
